import React from "react";
import { ReactElement } from "react";
import { MetadataFetch, ProfilesFetch, Metadata, MetadataMap, ValueMap, Field, CustomizationFetch, Customization, DownlinkData, ProfilesMap, ApplicationProfiles, Profile, UnpackCustomizationFetch, DipsApplications, DipsApplication, } from "./Fetch";
import { NumericInput } from "./NumericInput";
import { RadarCommandsInput } from "./RadarCommandsInput";
import { Progress } from "./Progress";
import { HelpBox } from "./HelpBox";
import { Headline } from "./Headline";
import { formatNumber, formatEmpty} from "../utils"
import { application } from "express";
import { ApplicationInput } from "./ApplicationInput";
import QRCode from "react-qr-code";

const libraryProfileIcon:string = "📁";
const customProfileIcon:string= "✍";

function hexToBase64(hexstring:string) {
    return btoa((hexstring.match(/\w{2}/g) as Array<string>).map(function(a) {
        return String.fromCharCode(parseInt(a, 16));
    }).join(""));
}

type DocumentationProps = {
    narrow: boolean;
    text: string;
}

const Documentation = (props:DocumentationProps) : ReactElement => {
    let [short, setShort] = React.useState<boolean>(true);
    let text : string = props.text;
    let shortened : boolean = false;
    if (text.length && text.length>200 && short) {
        text = text.substring(0, 160) + " ...";
        shortened = true;
    }

    return <div className={props.narrow ? "DocumentationNarrow":"DocumentationWide"} onClick={()=>{setShort(!short)}}>
        {text}<i>{shortened ? " Click to expand" : null}</i>
        </div>
}


type ApplicationSelectorProps = {
    applications : DipsApplications,
    metadata : MetadataMap,
    crc : string,
    selectApplication : Function,
    isDeveloper : boolean,
}

type MapAppNameAndVersionToCrc = {
    [crc : string] : string;
}
 
export const ApplicationSelector = (props : ApplicationSelectorProps) => {
    const [value, _setValue] = React.useState<string>(props.crc);
    const [editValue, setEditValue] = React.useState<string>(props.crc);

    const setValue = (value : string) => {
        if (mapNameAndVersionToCRC.hasOwnProperty(value)) {
            props.selectApplication(parseInt(mapNameAndVersionToCRC[value]));
        }
        _setValue(value);
    }

    let mapNameAndVersionToCRC : MapAppNameAndVersionToCrc = {};
    Object.keys(props.applications).map((crc : string) => {
        let app = props.applications[crc];
        if (!app)
            return null;
        let name : string = app.name;
        let versions : string = app.versions;
        if (!versions && !props.isDeveloper)
            return null;
        if (!props.metadata || !props.metadata.hasOwnProperty(crc))
            return null;
        if (/* !props.isDeveloper && */  (name === "Empty" || name === "Default" || name.includes("test") || name === "Tracker-stats") || name === "Sniffer")
            return null;
        if (typeof(name) === 'string' && typeof(versions) === 'string') {
            if (versions)
                versions = " " + versions;
            mapNameAndVersionToCRC[name + versions + " (" +  crc + ")"] = crc;
        }
        return null;
    });

    return <ApplicationInput
        completions={Object.keys(mapNameAndVersionToCRC).sort()} 
        title={"Application to Configure"} 
        info="The application can be uniquely identified by the first uplink the device sends after join, normally uplinked as device.vsm.rulesCrc32.
        If this information is missing you can instruct the device to re-send the information by posting a downlink 00 (hex) on port 15."
        setValue={setValue} value={value} editValue={editValue} setEditValue={setEditValue}/>;
}

type ConfigEditorProps = {
    metadata: Metadata;
    getValues: Function;
    setValues: Function;
    readonly : boolean;
    isDeveloper : boolean;
    applications: DipsApplications;
}

type EditValues = {
    [k:string] : string;
}

export const ConfigEditor = (props:ConfigEditorProps):ReactElement => {
    const [showRegisters /*, setShowRegisters*/] = React.useState<boolean>(props.isDeveloper);
    const [editValues, setEditValues] = React.useState<EditValues>({});
    const [applications, setApplications] = React.useState<DipsApplications>(props.applications);
    if (!props.metadata) {
        return <div></div>
    }
    const setValue = (k:string,v:number) => {
        let newValue = JSON.parse(JSON.stringify(props.getValues()));
        newValue[k] = v;
        props.setValues(newValue);
    }

    const setStringValue = (k:string, s:string) => {
        let newValue = JSON.parse(JSON.stringify(props.getValues()));
        newValue[k] = s;
        props.setValues(newValue);
    }

    const clearValue = (k:string) => {
        let newValue = JSON.parse(JSON.stringify(props.getValues()));
        delete newValue[k];
        props.setValues(newValue);
    }

    const isModified = (k:string) :boolean => {
        return props.getValues().hasOwnProperty(k);
    }

    const getMaxBySize = (size:number):number => {
        if (!size)
            size = 4;
        if (size === 1)
            return 0x7f;
        if (size === 2)
            return 0x7fff;
        if (size === 4)
            return 0x7fffffff;
        return 0;
    }
    const getMinBySize = (size:number):number => {
        return -getMaxBySize(size)-1;
    }

    const getMax = (size:number, translate:number, specifiedmax:number) : number => {
        let m : number = getMaxBySize(size);
        if (translate)
            m *= translate;
        if (isFinite(specifiedmax) && specifiedmax < m)
            m = specifiedmax;
        return m;
    }
    const getMin = (size:number, translate:number, specifiedmin:number) : number => {
        let m : number = getMinBySize(size);
        if (translate)
            m *= translate;
        if (isFinite(specifiedmin) && specifiedmin > m)
            m = specifiedmin;
        return m;
    }
    const getValue = (k:string) : string => {
        const field : Field = props.metadata.settings[k] as Field;
        if (!isFinite(props.getValues()[k])) {
            if (isFinite(field.default)) {
                return formatNumber(field.default, field.unit);
            }
            return formatEmpty(field.unit);
        }
        return formatNumber(props.getValues()[k], field.unit);
    }

    const getValueString = (k:string) : string => {
        if (typeof(props.getValues()[k]) !== "string")
            return "";
        return props.getValues()[k];
    }

    const settings = Object.keys(props.metadata.settings).map((k:string)=> {
        // Object info = props.metadata[k] as Object;
        const field : Field = props.metadata.settings[k] as Field;
        if (field.type === "register" && !showRegisters)
            return null;
        if (field.readOnly)
            return null;
        const max:number = getMax(field.size ? field.size : 4, field.translate, field.max);
        const min:number = getMin(field.size ? field.size : 4, field.translate, field.min);
        if (field.unit === "radar-xm122-commands")
            return <RadarCommandsInput
                key={props.metadata.appCRC + field.name}
                title={field.uiName ? field.uiName : field.name} 
                default={""}
                value={getValueString(k)} 
                setValue={(s:string) => setStringValue(k, s)}
                clearValue={()=> clearValue(k)}
                info={field.doc}
                showModified={true}
                isModified={()=>isModified(k)}
                readonly={props.readonly}
                editValue={editValues[k]}
                setEditValue={(v:string) => {
                let edits:EditValues = JSON.parse(JSON.stringify(editValues)) as EditValues;
                edits[k] = v;
                setEditValues(edits);}}
                />
        return <NumericInput 
                key={props.metadata.appCRC + field.name}
                title={field.uiName ? field.uiName : field.name} 
                minValue={min} 
                maxValue={max} 
                default={formatNumber(field.default, field.unit)+ ""} 
                value={getValue(k)} 
                setValue={(v:number) => setValue(k, v)}
                clearValue={()=> clearValue(k)}
                info={field.doc}
                unit={field.unit}
                showModified={true}
                isModified={()=>isModified(k)}
                readonly={props.readonly}
                editValue={editValues[k]}
                setEditValue={(v:string) => {
                    let edits:EditValues = JSON.parse(JSON.stringify(editValues)) as EditValues;
                    edits[k] = v;
                    setEditValues(edits);}}
                />
        });
    let settingsCount:number = 0;
    for (let i:number = 0; i < settings.length; ++i)
        if (settings[i])
            settingsCount++;

    if (settingsCount === 0) {
        return <div className="ConfigEditor">No settings for selected application</div>
    }
    return <div className="ConfigEditor">
        {settings}
        {props.readonly ? <div className="Hint">This profile is read-only.</div> : null}
        {props.readonly ? null : <div className="Hint">Edit values in decimal, or prefix with 0x for hexadecimal.</div> }
    </div>
}

type ProfileCardProps = {
    profile : Profile;
    onSelect : Function;
    isSelected : boolean;
}

const ProfileCard = (props : ProfileCardProps) : ReactElement => {
    return <div className={props.isSelected ? "ProfileCardSelected" : "ProfileCard"} onClick={()=>props.onSelect()}>
        <div className="Flexing2">
        <h3>{props.profile.name} {props.profile.configCrc32 ? (" - " + props.profile.configCrc32) : ""}</h3>
        <div className="IconAndInfo">{props.profile.icon ? props.profile.icon : libraryProfileIcon}</div>
        </div>
        {props.profile.description}
    </div>
}

type ProfilesLibraryProps = {
    profilesMap : ProfilesMap;
    getCurrentAppCrc : Function;
    getSelectedProfile : Function;
    setSelectedProfile : Function;
    isEdited : boolean;
}

const ProfilesLibrary = (props:ProfilesLibraryProps) : ReactElement => {
    const profiles : ApplicationProfiles = props.profilesMap[props.getCurrentAppCrc()] as ApplicationProfiles;
    const selectedProfile : Profile = props.getSelectedProfile();
    const setSelectedProfile = props.setSelectedProfile;
    const isEdited = props.isEdited;

    const onSelectProfile = (p : Profile) => {
        console.log("Select profile " + p.name);
        setSelectedProfile(p);
    }

    let cards : Array<ReactElement> = [];
    if (profiles) {
        let keys = Object.keys(profiles);
        keys.map((k:string)=>{
            const i = parseInt(k);
            cards.push(<ProfileCard 
                    profile={profiles[i] as Profile} 
                    onSelect={()=>onSelectProfile(profiles[i] as Profile)}
                    isSelected={profiles[i]?.data === selectedProfile?.data}/>)
            return 0;
        });
    }
    const custom : Profile = {port:7, custom:true, data:"", name:"Custom", 
            icon: (isEdited ? "... ":"") + customProfileIcon,
            description:"Custom Profile",appCrc32:0,configCrc32:0, warning:"Edit profile to generate downlinks"};
    cards.push(<ProfileCard isSelected={(!!selectedProfile) && selectedProfile.data===""} profile={custom} onSelect={()=>onSelectProfile(custom)} />);

    return <div>
        {cards}
    </div>
}

type ConfigToolProps = {
    token:string;
    rights:string;
    applications:DipsApplications;
    setProgress:Function;
}

type ReverseEngineeringProps = {
     crc:number;
     token:string;
     setValues:Function;
     selectCustomProfile:Function;
     profileByDownlinkMap:ProfileByDownlinkMap;
     setApplicationCrc:Function;
     metadataMap:MetadataMap;
}

const ReverseEngineering = (props:ReverseEngineeringProps) : ReactElement => {
    const emptyText = "Enter downlink..."
    const [port, setPort] = React.useState<number>(7);
    const [data, setData] = React.useState<string>("");
    const [applying, setApplying] = React.useState<boolean>(false);
    const [error, setError] = React.useState<string>(emptyText);

    const onApply = () => {
        setApplying(true);
        props.setValues({});
    }
    const getHint = (data : string) : string => {
        if (data.length === 0 || (data.length%2 === 1))
            return emptyText;
        if (props.profileByDownlinkMap.hasOwnProperty(data)) {
            const profile : Profile = props.profileByDownlinkMap[data] as Profile;
            return "Profile " + profile.name + " (app " + profile.appCrc32 + ")";
        } 
        return "Unknown profile...";
    }
    const getApplicationCrc = ():number => {
        if (port === 7 && data.length > 16) {
            return parseInt(data.substring(0,8),16);
        }
        return NaN;
    }

    const isApplicable = () => {
        if (port === 7) {
            const linkCrc = getApplicationCrc();
            return (props.crc===linkCrc);
        }
        return true;
    }
    const onChangeApp = (crc:number) => {
        props.setApplicationCrc(crc);
    }
    const getApplicationName = (crc:number) => {
        if (props.metadataMap && props.metadataMap.hasOwnProperty(crc))
            return "(" + (props.metadataMap[crc] as Metadata).name + ")";
        return "";
    }

    return <div>
        <div key={error}><small>{error}</small></div>
        <input type="number" value={port} placeholder="Port (7 typically)" onChange={(e)=>{setPort(parseInt(e.target.value));setError("Editing...")}}/>
        <input value={data} placeholder="Hexdata" onChange={(e)=>{setData(e.target.value); setError(getHint(e.target.value))}}/>
        {(port === 7 && data.length>16 && !isApplicable() && getApplicationCrc()) ? 
            <div className="Hint">Downlink for other app {getApplicationCrc()} {getApplicationName(getApplicationCrc())}</div> : null}
        <div className="Horizontal">
        {<div className="Button" onClick={()=>applying || onApply()}>Apply</div>}
        {port ===7 && data.length>16 && !(isApplicable()) ? <div className="Button" onClick={()=>applying || onChangeApp(getApplicationCrc())}>Switch</div> : null}
        </div>
        {applying ? <UnpackCustomizationFetch 
                        token={props.token} 
                        crc={props.crc} 
                        port={port} 
                        data={data}
                        onError={(s:string)=>{
                            setError(s); setApplying(false);console.log("**** ", s); props.setValues({})}}
                        onValues={(v:ValueMap)=>{
                            setError("Custom Profile Updated...");
                            console.log(error);
                            if (v) {
                                props.setValues(v);
                                props.selectCustomProfile(port, data);
                            }
                            setApplying(false);
                        }}/> : null}
        <br></br>
    </div>
}

type SelectedProfile = {
    port: number;
    data: string;
    name: string;
    custom: boolean;
    warning: string | undefined;
}

type ProfileByDownlinkMap = {
    [downlink: string]: Profile | undefined;
}

function getWindowDimensions() {
  const { innerWidth: width, innerHeight: height } = window;
  return {
    width,
    height
  };
}

export default function useWindowDimensions() {
  const [windowDimensions, setWindowDimensions] = React.useState(getWindowDimensions());

  React.useEffect(() => {
    function handleResize() {
      setWindowDimensions(getWindowDimensions());
    }

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return windowDimensions;
}

type QRButtonProps = {
    appName : string;
    appCRC : string;
    name : string;
    port : number;
    data : string;
};

const QRButton = (props:QRButtonProps) => {
    const [expanded, setExpanded] = React.useState(false);
    const [qrName, setQrName] = React.useState<string>(props.name);
    const [nameSet, setNameSet] = React.useState<boolean>(false);
    
    const qrObj = {...props, name:qrName}

    if (!expanded)
        return <div className="QRButtonClosed" onClick={()=>setExpanded(true)}>QR ▣</div>;
    return <div className="QRButtonExpanded" onClick={()=>setExpanded(false)}>QR Code for {props.appName} / {props.name} profile. Scan on device with Hugin NFC app.
                <div className="ModalDialogBackground">
                    <div className="QRDialogBox">
                            {nameSet ? <div className="Hint"><center>Scan this code on a phone with the Hugin NFC application to store profile '{qrObj.name}' in the phone.</center></div> : null}
                            {nameSet ? <QRCode size={2048}
                                style={{ height: "25vh", maxWidth: "25vh", width: "25vh" }}
                                value={"https://ruti.se/dots/config?json=" + encodeURIComponent(JSON.stringify(qrObj))}
                                viewBox={`0 0 256 256`}/> : null}
                            {nameSet ? <div><br/><button className="QrNameSetButton" onClick={(e)=>{e.stopPropagation(); setNameSet(false)}}>Rename</button></div> : null}

                            {!nameSet ? <center><div className="QrNameInputBox">
                                <div className="NumericInputTitle">Name this Config</div>
                                <input className="QrNameInput" type="text" maxLength={32} value={qrName} onClick={(e)=>{e.preventDefault(); e.stopPropagation()}} onChange={(e)=>setQrName(e.target.value)}/>
                                { /* <button className="QrNameSetButton" onClick={(e)=>{e.stopPropagation(); setQrName(props.name)}}>Default</button> */ }
                                <button className="QrNameSetButton" onClick={(e)=>{e.stopPropagation(); setNameSet(true)}}>OK</button>
                                </div></center>: null}
                            <div className="Hint"><center>Click anywhere to close this box.</center></div>
                        </div> 
                    </div>
            </div>;
}

export const ConfigTool = (props:ConfigToolProps):ReactElement => {
    const queryParameters = new URLSearchParams(window.location.search);
    const initialCrc : number = queryParameters.get("app") ? parseInt(queryParameters.get("app") as string) : 0;
  
    const [metadata, setMetadata] = React.useState<MetadataMap>();
    const [crc, _setCrc] = React.useState<number>(initialCrc);
    const [values, _setValues] = React.useState<ValueMap>({});
    const [customization, _setCustomization]  = React.useState<Customization>();
    const [pending, setPending] = React.useState<boolean>(false);
    const [base64, setBase64] = React.useState<boolean>(false);
    const [showAsDownlinks, setShowDownlinks] = React.useState<boolean>(false);
    const [profilesMap, _setProfilesMap] = React.useState<ProfilesMap>();
    const [profilesByDownlink, setProfilesByDownlink] = React.useState<ProfileByDownlinkMap>();
    const [selectedProfile, _setSelectedProfile] = React.useState<SelectedProfile>();
    const [profileValues, setProfileValues] = React.useState<ValueMap>();
    const { height, width } = useWindowDimensions();
    const [crcEdits, setCrcEdits] = React.useState<string>("");
    const [convertToCustomPending, setConvertToCustomPending] = React.useState<boolean>(false);

    const setCrc = (c:number) => {
        setValues({});
        setPending(false);
        setCustomization(undefined, false);
        setProfileValues(undefined);
        _setSelectedProfile(undefined);
        _setCrc(c);
    }

    const setProfilesMap = (p:ProfilesMap) => {
        _setProfilesMap(p);
        // Also, generate a reversed map, by downlink to profile (ignore port for now)
        if (p) {
            const appCrcs:Array<string> = Object.keys(p);
            let mapped = {} as ProfileByDownlinkMap;
            appCrcs.map((k:string)=>{
                const configCrcs:Array<string> = Object.keys(p[parseInt(k)] as ApplicationProfiles);
                configCrcs.map((c) => {
                    const downlink = (p[parseInt(k)] as ApplicationProfiles)[parseInt(c)]?.data;
                    if (downlink) {
                        mapped[downlink] = (p[parseInt(k)] as ApplicationProfiles)[parseInt(c)];
                    }
                    return undefined;
                })
                return undefined;
            });
            setProfilesByDownlink(mapped);
        }
    }

    const setCustomization = (c : Customization|undefined, autoselect : boolean) => {
        _setCustomization(()=>c);
    }

    const setValues = (v:ValueMap) => {
        _setValues(v);
        setPending(true);
    }

    const setSelectedProfile = (p : Profile|undefined) => {
        if (selectedProfile && p)
            setProfileValues(undefined);
        if (p)
            _setSelectedProfile({name:(p as Profile).name, port:p.port, data:p.data, custom:p.custom ? true:false, warning:p.warning});
        else
            _setSelectedProfile(undefined);
        setProfileValues(undefined);
    }

    if (!metadata) {
        return <div>
                <Progress text="Loading" subtext="Application Metadata..."/>
                <MetadataFetch token={props.token} setMetadata={(d:MetadataMap) => {setMetadata(d)}} onError={(e:string)=>props.setProgress("Error", e)}/>
            </div>;
    }
    if (!profilesMap) {
        return <div>
                <Progress text="Loading" subtext="Application Profiles..."/>
                <ProfilesFetch token={props.token} onProfilesMap={(p:ProfilesMap)=>setProfilesMap(p)} onError={(e:string)=>props.setProgress("Error", e)}/>
            </div>;
    }
    const formatHexOrBase64 = (s : string) => {
        if (!base64)
            return s;
        return hexToBase64(s);
    }

    const formatCustomization = () : string => {
        if (selectedProfile && !selectedProfile.custom && selectedProfile.data) {
            if (selectedProfile.warning)
                return selectedProfile.warning;
            return "Profile " + selectedProfile.name + " - Port: " + selectedProfile.port + " Data: " + formatHexOrBase64(selectedProfile.data) 
                    + " (" + selectedProfile.data.length/2 + " bytes)";
        }
        if (!customization || !customization.port || !customization.data)
            return "";
        if (customization.warning)
            return customization.warning;
        return "Custom downlink - Port " + customization.port + " Data " + formatHexOrBase64(customization.data)
                    + " (" + customization.data.length/2 + " bytes)";
    }

    const formatDownlinks = () : string => {
        let text = "Downlinks (non-persistent settings):\n";
        if (!customization || !Array.isArray(customization.downlinks))
            return "No split downlinks avaialable";
        if (customization.warning)
            return customization.warning;
        customization.downlinks.map((d:DownlinkData) => text += "Port " + d.port + " Data " + formatHexOrBase64(d.data) + "\n");

        return text;
    }

    const selected:Metadata = metadata[""+crc] as Metadata;

    const showLibrary = () : boolean => {
        return !!selected;
    }

    const showContent = () : boolean => {
        return showLibrary() && (selectedProfile ? true : false);
    }

    const getEditValues = () : ValueMap => {
        if (selectedProfile && !selectedProfile.custom) {
            if (!profileValues) {
                console.log("No profile values available")
                return {};
            }
            return profileValues as ValueMap;
        }
        return values;
    }

    const formatJsonFileName = () : string => {
        if (customization) {
            let cmzCrc32 = parseInt(customization.data.substring(customization.data.length-8, customization.data.length), 16);
            return crc + "/" + cmzCrc32 + ".json";
        }
        return crc + "";
     }
    const formatJsonFile = () : string => {
        if (customization) {
            let object = {
                name:"",
                description:"",
                port:customization.port,
                data:customization.data,
                warning: "",
            };
            if (customization.warning) {
                object.warning = customization.warning;
            }
            let s : string = JSON.stringify(object);
            return s.replaceAll(',',',\n').replaceAll("{", "{\n").replaceAll("}", "\n}").replaceAll("\n\"", "\n  \"");
        }
        else return "";
    }

    const formatted : string = showAsDownlinks ? formatDownlinks() : formatCustomization();
    const isReadOnlyProfile:boolean = selectedProfile !== undefined && !selectedProfile.custom;
    const isDeveloper : boolean = props.rights.includes("D") || props.rights.includes("V");
    const isLibraryAvailable : boolean = crc!==0 && profilesMap!==undefined && profilesMap.hasOwnProperty(crc+"");

    const copyToCustomProfile = (port:number, data:string) => {
        if (selectedProfile && isReadOnlyProfile) {
            // Do reverse engineering
            setConvertToCustomPending(true);
        }
    }

    const printIfIdenticalToProfile = () =>{ 
        // If the customization is same as a profile, select that profile automatically
        if (!isReadOnlyProfile && profilesMap && crc && profilesMap[crc]) {
            let profileIndexes = Object.keys(profilesMap[crc as number] as ApplicationProfiles);
            for (let i:number = 0; i < profileIndexes.length; ++i) {
                const index : number = parseInt(profileIndexes[i]);
                const profile : Profile = (profilesMap[crc] as ApplicationProfiles)[index] as Profile;
                if (customization && profile.data === customization.data && profile.port === customization.port) {
                    return <small><i>{"Identical to profile " + profile.name + " - " + profile.configCrc32}</i></small>;
                }
            }
        }
        return null;
    }

    return <div className={width>height?"BodyAreaHorizontal": "BodyAreaVertical"}>

    <div className="Browser">
        <h2>Application</h2>
        { props.applications ? <ApplicationSelector metadata={metadata} applications={props.applications} crc={""+crc} selectApplication={(crc:number)=>setCrc(crc)} isDeveloper={isDeveloper} /> : null}
        {/* isDeveloper && <NumericInput   minValue={1} maxValue={Number.MAX_SAFE_INTEGER} 
                        setValue={(v:number)=>{setCrc(v); setPending(false); setValues({});}} value={crc ? crc+"":""}
                        title="Application CRC" info="Unique number identifying the application to configure. Normally uplinked as device.vsm.rulesCrc32"
                        validate={(n:number)=>metadata.hasOwnProperty(n)}
                        invalidString="Application CRC"
                        completions={metadata ? Object.keys(metadata):[]}
                        editValue={crcEdits}
                        setEditValue={(v:string)=>setCrcEdits(v)}/> */} 

        { selected ? null : <div className="Hint">Enter a Application CRC to select application.</div>}
        { selected ? <div className="PublicActionArea" key={crc}>
        <h3>Application Info</h3>
        <div className="ApplicationNameAndHelp" key={crc+"help"}>
        { selected.name }
        { <a target="manual" href={`https://github.com/Sensative/vsm-application-documentation/blob/master/${crc}-${selected.name}.vso.md`}><div className="HelpBoxClosed">?</div></a>}
        </div>
        <div className="Versions">
        { crc && props.applications.hasOwnProperty(crc) ? <small>{(props.applications[crc] as DipsApplication).versions}</small> : null}
        </div> 
        { selected && selected.hasOwnProperty("doc") ? <Documentation text={selected.doc as string} narrow={width>height}/> : null}
        </div> : null }
        <div style={{textAlign:"left"}}>
        { isDeveloper && (!!profilesByDownlink) &&
                <div className="ActionArea">
                    <div className="TextWithHelpBox">
                    <h3>Reverse Engineer from Downlink</h3>
                        <HelpBox name="Developer/Verifyer Access" info="Use a downlink to initialize the Custom profile, find an existing profile, or convert customization between app versions"></HelpBox>
                    </div>
                    <ReverseEngineering 
                        crc={crc ? crc : 0} token={props.token} 
                        setValues={(v:ValueMap)=>{ setValues(v) }}
                        selectCustomProfile={(port:number, data:string)=>{
                            setSelectedProfile(undefined);
                        }}
                        profileByDownlinkMap={profilesByDownlink as ProfileByDownlinkMap}
                        setApplicationCrc={(v:number)=>{ setCrc(v); setCrcEdits(v+"");setSelectedProfile(undefined)}}
                        metadataMap={metadata}
                        />
                    <br></br>
                </div>
        }
        { isDeveloper && crc && customization &&
                <div className="ActionArea">
                    <div className="TextWithHelpBox">
                    <h3>Custom Profile File Editor</h3>
                        <HelpBox name="Developer Option" info="A template file for adding to the git dots-config-repository (add name and description, paste in local dots-config-repository, manually commit)"></HelpBox>
                        {(false && window.location.host.includes("localhost")) ? <div className="SaveButton">↓</div> : null}
                        </div>
                    <small>{formatJsonFileName()}</small>
                    <textarea key={formatJsonFile()} className={"JsonTextArea"} rows={10} defaultValue={formatJsonFile()}></textarea>
                    <br></br>
                </div>
        }
        </div>
    </div>

    {showLibrary() ? <div className="ProfileLibrary">
        <h2>Profile Library</h2>
        <ProfilesLibrary 
            profilesMap={profilesMap} 
            getCurrentAppCrc={()=>crc} 
            getSelectedProfile={()=>selectedProfile}
            setSelectedProfile={(p:Profile)=>setSelectedProfile(p)}
            isEdited={values && Object.keys(values).length>0}
            />
        {(!(selectedProfile || isLibraryAvailable)) ? <div className="Hint">No predefined profiles for this application. Create a custom profile?</div> : null}
        { selectedProfile ? null : <div className="Hint">Click on a profile to select it.</div>}
        </div>
     : null}
    {showContent() ? <div className="Settings">
        <div className="Flexing2">
        <h2>{selectedProfile ? selectedProfile.name : "Custom "} Profile Settings</h2>
        {isReadOnlyProfile ? <div className="Customize" onClick={()=>{copyToCustomProfile(selectedProfile?.port as number, selectedProfile?.data as string);}}>... {customProfileIcon}<br/>Copy to Custom</div> : null}
        {printIfIdenticalToProfile()}
        </div>
        {<div><ConfigEditor 
            metadata={selected} 
            readonly={isReadOnlyProfile}
            getValues={()=>getEditValues()} 
            setValues={setValues}
            isDeveloper={isDeveloper}
            applications={props.applications}
            /></div>}
    </div> : null }
    { formatted !== "" ? <div className="Downlink">
        {formatted}
        {(selectedProfile && customization && customization.data && customization.port && !customization.warning) ? 
            <QRButton appCRC={""+selected.appCRC} appName={selected.name} name={selectedProfile.name} data={customization.data} port={customization.port}/> : null}
        <div className="HexOrBase64Area"><div className="HexOrBase64Button" onClick={()=>setBase64(!base64)}>{base64 ? "Base64" : "Hex"}</div></div>
        <div className="ShowAsDownlinksArea"><div className="HexOrBase64Button" onClick={()=>setShowDownlinks(!showAsDownlinks)}>{showAsDownlinks ? "Split" : "Single"}</div></div>
    </div> : null}
    <Headline isDeveloper={isDeveloper}/>
    {pending && <CustomizationFetch token={props.token} 
        onCustomization={(c:Customization)=>{setCustomization(c, false); setPending(false);}}
        onError={(s:string)=>{setCustomization(undefined, false);setPending(false); console.log(s)}}
        settings={values}
        crc={crc ? crc : 0}/>}
    {(isFinite(crc as number) && selectedProfile && !selectedProfile.custom && !profileValues) ?
        <UnpackCustomizationFetch 
            data={selectedProfile.data} 
            port={selectedProfile.port} 
            crc={crc as number}
            onError={(s:string)=>console.log(s)}
            onValues={(v:ValueMap)=>setProfileValues(v)}
            token={props.token}
            /> : null
        }
    {(convertToCustomPending && selectedProfile && !selectedProfile.custom) ? 
            <UnpackCustomizationFetch 
                token={props.token} 
                crc={crc} 
                port={selectedProfile.port} 
                data={selectedProfile.data}
                onError={(s:string)=>{
                    console.log("**** ", s); setConvertToCustomPending(false);}}
                onValues={(v:ValueMap)=>{
                    if (v) {
                        setValues(v);
                        setSelectedProfile(undefined);
                    }
                    setConvertToCustomPending(false);
                }}/> : null                
        }

    </div>
}

